// ============================================================
// DispensaryMap — single imperative mapbox-gl map
// ============================================================
// react-map-gl is intentionally NOT used here (it remains only in
// LocationWizard). The declarative <Source>/<Layer> model calls
// map.removeSource() whenever React unmounts the element — under
// StrictMode double-mounts and theme setStyle() swaps that hits
// mapbox-gl's removeSource → _updateSources → updateTerrain path on a
// half-torn-down style and crashes ("Cannot read properties of
// undefined (reading 'get')"). Here the map is created ONCE, sources
// and layers are added imperatively on every style.load, pin data
// flows through setData, and the dispensary source is NEVER removed —
// teardown is a single map.remove(), which disposes the whole GL
// context cleanly. The crash path no longer exists.

import {
  Component,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import mapboxgl from 'mapbox-gl'
import type {
  EasingOptions,
  GeoJSONFeature,
  GeoJSONSource,
  MapMouseEvent,
  Map as MapboxMap,
} from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {
  MAPBOX_TOKEN, isMapConfigured, MAP_STYLES, EMPTY_FC, TRACK_STATE_COLORS, type MapStyleId,
} from './mapStyle'
import { SlotConfirmDialog, type SlotConfirmData, type SameMenuPin } from './SlotConfirmDialog'
import { OwnLocationMarker } from './OwnLocationMarker'
import { isTrackingAction } from '../../lib/trackingOnly'
import { isOwnedRivalCandidate } from './ownLocationMatch'
import { useAppTheme } from '../../hooks/useAppTheme'
import { useDispensaryMap, type DispensaryFeatureCollection } from '../../hooks/useDispensaryMap'
import {
  SOURCE,
  dispensaryLayerStack,
  dispensaryPointLayer,
  dispensaryEnrichedOnlyPointLayer,
  type MapLayerSpec,
} from './layers'
import {
  ZOOM,
  CALIFORNIA_VIEWPORT,
  trackStateToFlags,
  flagsToTrackState,
  parseCompetitorIds,
  type DispensaryFeatureProps,
  type TrackBlockFlags,
} from './types'

const API = import.meta.env.VITE_API_URL ?? ''

// ── Public contract types ─────────────────────────────────────────────────────

/** Map interaction event forwarded to pages — mapbox-gl native event with
 *  `features` attached from queryRenderedFeatures over interactive layers. */
export type MapInteractionEvent = MapMouseEvent & { features?: GeoJSONFeature[] }

/** A page-owned GeoJSON overlay (markets bubbles, radius ring, …). The
 *  overlay id doubles as the source id; layers are re-added on every
 *  style.load and data flows through setData. */
export interface MapOverlaySpec {
  id: string
  data: GeoJSON.FeatureCollection
  layers: MapLayerSpec[]
}

/** A page-owned DOM marker (own-location pin, …). `element` is rendered
 *  into a mapboxgl.Marker container via a React portal. */
export interface MapMarkerSpec {
  id: string
  longitude: number
  latitude: number
  element: React.ReactNode
  onClick?: () => void
}

/** Hover-style popup (no close button, pointer-events: none). */
export interface MapHoverPopupSpec {
  longitude: number
  latitude: number
  content: React.ReactNode
}

/** Imperative surface exposed via ref / the `mapRef` prop. Keeps the
 *  method names pages already call (`flyTo`, `getMap`). */
export interface DispensaryMapHandle {
  getMap(): MapboxMap | null
  flyTo(options: EasingOptions): void
  easeTo(options: EasingOptions): void
  resize(): void
  setOverlayData(id: string, data: GeoJSON.FeatureCollection): void
  /** THE one track/block action path — same code the built-in popup runs.
   *  Pages with their own buttons (CD sidebar) call this so display state
   *  and mutation logic can never fork. Resolves the competitor from the
   *  feature's server-matched competitor_ids, applies the API change,
   *  patches the pin optimistically, then reconciles via refetch. */
  toggleTrackBlock(
    props: DispensaryFeatureProps,
    action: 'track' | 'block',
    locationId?: string,
  ): Promise<boolean>
  /** Confirm-gated entry point (2C). Opens the billing confirm dialog; the
   *  slot change + recolor run only after the operator confirms. Pages with
   *  their own buttons (CD sidebar) call this so the confirm flow can't be
   *  bypassed. */
  requestToggle(
    props: DispensaryFeatureProps,
    action: 'track' | 'block',
    locationId?: string,
  ): void
  /** Remove a batch of existing slots by (location, competitor, slot type) —
   *  drives the SAME DELETE endpoint + the SAME reconciliation (map refetch,
   *  competitor + summary refresh) that `toggleTrackBlock`'s removal branch
   *  uses; no separate mutation path. Used by the slot-manager modal, which
   *  already knows the exact slots to drop (so it doesn't need a pin's
   *  DispensaryFeatureProps). Resolves true if at least one slot was removed. */
  removeSlots(
    targets: { locationId: string; competitorId: string; slotType: 'track' | 'block' }[],
  ): Promise<boolean>
}

/** One active slot row — which location holds it and of which type.
 *  DELETE /locations/:id/competitors/:cId is per-location, so untrack and
 *  unblock must target the location(s) that actually hold the slot. */
export interface CompetitorSlot {
  location_id?: string
  slot_type: string
  tracked_id?: string
}

/** Competitor row as returned by GET /locations/:id/competitors, plus the
 *  order-independent merged slot set (RC3). */
export interface PopupCompetitor {
  tracked_id?: string
  competitor_id: string
  name: string
  lat?: number | string | null
  lng?: number | string | null
  slot_type?: string
  /** The org location whose fetch produced this row (added client-side). */
  location_id?: string
  /** Union of slot types across all locations — order-independent. */
  slot_types?: string[]
  /** Per-location slot rows across all locations (merged). */
  slots?: CompetitorSlot[]
  blocked_at?: string | null
  business_type?: string | null
  google_place_id?: string | null
  /** Legacy market tier retained in the API row; it does not affect pricing. */
  market_tier?: string | null
  price_per_slot?: number | string | null
}

/** Order-independent merge of per-location competitor rows (RC3).
 *  Dedupes by competitor_id, unions slot types into `slot_types`, and keeps
 *  every per-location slot row in `slots` so removals can target the
 *  location that holds the slot. */
export function mergeCompetitorRows<T extends {
  competitor_id: string; slot_type?: string; location_id?: string; tracked_id?: string
}>(
  rows: T[],
): (T & { slot_types: string[]; slots: CompetitorSlot[] })[] {
  const byId = new Map<string, T & { slot_types: string[]; slots: CompetitorSlot[] }>()
  for (const row of rows) {
    const slot: CompetitorSlot | null = row.slot_type
      ? { location_id: row.location_id, slot_type: row.slot_type, tracked_id: row.tracked_id }
      : null
    const existing = byId.get(row.competitor_id)
    if (existing) {
      if (row.slot_type && !existing.slot_types.includes(row.slot_type)) {
        existing.slot_types.push(row.slot_type)
      }
      if (slot) existing.slots.push(slot)
    } else {
      byId.set(row.competitor_id, {
        ...row,
        slot_types: row.slot_type ? [row.slot_type] : [],
        slots: slot ? [slot] : [],
      })
    }
  }
  return [...byId.values()]
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface DispensaryMapProps {
  authFetch?: (url: string, init?: RequestInit) => Promise<Response>
  filters?: { type?: string; enriched?: boolean; q?: string; refreshKey?: number }
  enrichedOnly?: boolean
  pinMinZoom?: number
  initialViewState?: { longitude: number; latitude: number; zoom: number }
  showStyleToggle?: boolean
  cursor?: string
  style?: React.CSSProperties
  extraInteractiveLayers?: string[]
  onPinClick?: (props: DispensaryFeatureProps, lngLat: { lng: number; lat: number }) => void
  renderPopup?: (props: DispensaryFeatureProps, close: () => void) => React.ReactNode
  /** Fired once per map INSTANCE at the first of styledata/idle/load —
   *  i.e. as soon as camera operations are reliable. The bare 'load' event
   *  waits on every initial tile, which throttled Mapbox responses can delay
   *  indefinitely; pages keying their fly-to on this must not wait that long.
   *  StrictMode pass-2 maps fire it again, so effects keyed on a counter
   *  bumped here re-center the replacement instance. */
  onMapLoad?: () => void
  onMoveEnd?: (ctx: { zoom: number }) => void
  onMapClick?: (e: MapInteractionEvent) => void
  onMapMouseMove?: (e: MapInteractionEvent) => void
  onMapMouseLeave?: () => void
  onDispensariesChange?: (fc: DispensaryFeatureCollection, loading: boolean) => void
  onPopupAction?: () => void
  mapRef?: React.MutableRefObject<DispensaryMapHandle | null>
  /** Page-owned org locations (RC2). When provided, DispensaryMap does NOT
   *  self-fetch /locations — the page owns the data. `lat`/`lng` (when present)
   *  drive the pulsing own-location markers rendered by the shared component. */
  locations?: { id: string; name: string; lat?: number | string | null; lng?: number | string | null }[]
  /** Page-owned merged competitors (RC2). When provided, DispensaryMap does
   *  NOT self-fetch per-location /competitors. */
  competitors?: PopupCompetitor[]
  /** Page-owned refresh for `competitors` — called after popup actions. */
  refreshCompetitors?: () => Promise<void> | void
  /** Fired whenever the (internal or external) merged competitor list
   *  changes — lets pages consume the single internally-fetched list
   *  (e.g. CommandCenter search) instead of duplicating the fetch. */
  onCompetitorsChange?: (competitors: PopupCompetitor[]) => void
  /** Pin all popup/handle track-block actions to this location (e.g. the
   *  location selected in CompetitorDiscovery). Hides the popup's own
   *  location dropdown — the page controls the target. */
  actionLocationId?: string
  /** Page GeoJSON overlays (replaces react-map-gl <Source>/<Layer> children). */
  overlays?: MapOverlaySpec[]
  /** Page DOM markers (replaces react-map-gl <Marker> children). */
  markers?: MapMarkerSpec[]
  /** Hover popup (replaces react-map-gl <Popup closeButton={false}> children). */
  hoverPopup?: MapHoverPopupSpec | null
  /** Non-geo overlay DOM only (buttons, legends) — rendered above the canvas. */
  children?: React.ReactNode
}

// ── Error boundary (minimal — render errors only) ─────────────────────────────
// The imperative core has almost no React render surface that can throw
// (marker/popup portals, page renderPopup). No auto-retry, no recoveryKey:
// resetting simply remounts the core, which builds a fresh map.

class MapErrorBoundary extends Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error: Error) { return { error } }
  componentDidCatch(error: Error) {
    console.error('[DispensaryMap]', error.message)
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)', gap: 12 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Map failed to load</div>
          <button
            onClick={() => this.setState({ error: null })}
            style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontFamily: 'var(--sans)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-1)' }}
          >
            Reload map
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// ── Core component ────────────────────────────────────────────────────────────

interface CoreProps extends DispensaryMapProps {
  forwardedRef: React.ForwardedRef<DispensaryMapHandle>
}

function DispensaryMapCore({
  authFetch,
  filters,
  enrichedOnly = false,
  pinMinZoom = ZOOM.METRO,
  initialViewState,
  showStyleToggle = false,
  cursor,
  style,
  extraInteractiveLayers,
  onPinClick,
  renderPopup,
  onMapLoad,
  onMoveEnd,
  onMapClick,
  onMapMouseMove,
  onMapMouseLeave,
  onDispensariesChange,
  onPopupAction,
  mapRef: externalMapRef,
  locations: externalLocations,
  competitors: externalCompetitors,
  refreshCompetitors: externalRefreshCompetitors,
  onCompetitorsChange,
  actionLocationId,
  overlays,
  markers,
  hoverPopup,
  children,
  forwardedRef,
}: CoreProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const mapNodeRef = useRef<HTMLDivElement | null>(null)
  const mapObjRef = useRef<MapboxMap | null>(null)
  const [mapReady, setMapReady] = useState(false)

  const appTheme = useAppTheme()
  const [mapStyleId, setMapStyleId] = useState<MapStyleId>('streets')

  // Style captured at mount; later changes go through map.setStyle() only.
  const initialStyleRef = useRef(MAP_STYLES['streets'][appTheme])
  const appliedStyleRef = useRef(initialStyleRef.current)
  const initialViewRef = useRef(initialViewState)

  // Latest pin data — seeds the source on every style.load, no refetch.
  const latestDispensariesRef = useRef<DispensaryFeatureCollection>(EMPTY_FC as DispensaryFeatureCollection)

  // ── Popup / lifted data state ─────────────────────────────────────────────

  const usingExternalLocations = externalLocations !== undefined
  const usingExternalCompetitors = externalCompetitors !== undefined
  // Competitor rows are needed for action RESOLUTION (which competitor/
  // location a toggle hits) on every authenticated map — including pages
  // that render their own popup but call handle.toggleTrackBlock.
  const shouldLoadPopupState = Boolean(authFetch)

  const [internalLocations, setInternalLocations] = useState<{ id: string; name: string; lat?: number | string | null; lng?: number | string | null }[]>([])
  const [internalCompetitors, setInternalCompetitors] = useState<PopupCompetitor[]>([])
  const locations = externalLocations ?? internalLocations
  const competitors = externalCompetitors ?? internalCompetitors

  const [trackLocId, setTrackLocId] = useState<string>('')
  const [savingTrack, setSavingTrack] = useState(false)
  const [dispPopup, setDispPopup] = useState<{ lng: number; lat: number; props: DispensaryFeatureProps } | null>(null)
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null)
  const [internalRefresh, setInternalRefresh] = useState(0)
  // Confirm-before-change: the pending action's dialog data + the
  // executor to run on confirm. Set by requestToggle, cleared on confirm/cancel.
  const [confirm, setConfirm] = useState<{ data: SlotConfirmData; run: () => Promise<void> } | null>(null)

  // ── Live props ref — map event handlers read these at call time ──────────

  const propsRef = useRef({
    cursor, enrichedOnly, pinMinZoom, extraInteractiveLayers, overlays,
    onPinClick, onMapLoad, onMoveEnd, onMapClick, onMapMouseMove, onMapMouseLeave,
  })
  propsRef.current = {
    cursor, enrichedOnly, pinMinZoom, extraInteractiveLayers, overlays,
    onPinClick, onMapLoad, onMoveEnd, onMapClick, onMapMouseMove, onMapMouseLeave,
  }

  // ── Dispensary data (bbox-debounced) ──────────────────────────────────────

  // The selected own-location: the store all popup/sidebar actions target and
  // the store the pins reflect (MULTI_LOCATION_BILLING_SPEC §3 Phase 3). A page
  // that pins a location (CompetitorDiscovery's actionLocationId) wins; else the
  // popup's location dropdown (trackLocId, default = locations[0]). When known,
  // it scopes track_state to this store's TRUE per-location state.
  const selectedLocationId = actionLocationId ?? trackLocId

  const [bbox, setBbox] = useState<string | null>(null)
  const { data: dispensaries, loading: dispensariesLoading } = useDispensaryMap(bbox, {
    type: filters?.type,
    enriched: filters?.enriched,
    q: filters?.q,
    refreshKey: (filters?.refreshKey ?? 0) + internalRefresh,
    locationId: selectedLocationId || undefined,
    fetchFn: authFetch,
  })

  // ── Applied FeatureCollection — THE single source of truth ───────────────
  // What the pins actually show: latest server fetch + optimistic patches.
  // Every display surface reads it — pin layers (via setData), the popup
  // badge/buttons (via livePopupProps), and pages (via onDispensariesChange,
  // e.g. the CompetitorDiscovery sidebar). They cannot disagree.
  const [appliedFc, setAppliedFc] = useState<DispensaryFeatureCollection>(
    EMPTY_FC as DispensaryFeatureCollection,
  )

  useEffect(() => { onDispensariesChange?.(appliedFc, dispensariesLoading) }, [appliedFc, dispensariesLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  // Push new pin data into the long-lived source. A transient EMPTY
  // collection while a fetch is in flight must NOT overwrite a populated
  // ref — the style.load reseed would paint 0 pins mid theme-swap (#2).
  const lastAppliedFcRef = useRef<DispensaryFeatureCollection | null>(null)
  useEffect(() => {
    if (lastAppliedFcRef.current === dispensaries) return
    if (
      dispensaries.features.length === 0 &&
      dispensariesLoading &&
      latestDispensariesRef.current.features.length > 0
    ) return
    lastAppliedFcRef.current = dispensaries
    latestDispensariesRef.current = dispensaries
    setAppliedFc(dispensaries)
    const src = mapObjRef.current?.getSource(SOURCE.DISPENSARIES) as GeoJSONSource | undefined
    src?.setData(dispensaries as unknown as GeoJSON.FeatureCollection)
  }, [dispensaries, dispensariesLoading])

  // ── Sources & layers — added once per style, never removed ────────────────

  const overlayStateRef = useRef<Map<string, { layerIds: string[]; data: unknown }>>(new Map())

  const applyOverlays = useCallback((map: MapboxMap) => {
    const specs = propsRef.current.overlays ?? []
    const applied = overlayStateRef.current

    // Remove overlays whose id is gone (page overlays only — the dispensary
    // source is NEVER removed). Guarded so a fresh style is a no-op.
    for (const [id, state] of [...applied]) {
      if (!specs.some(s => s.id === id)) {
        for (const lid of state.layerIds) if (map.getLayer(lid)) map.removeLayer(lid)
        if (map.getSource(id)) map.removeSource(id)
        applied.delete(id)
      }
    }

    // Insert overlay layers beneath the dispensary stack so pins stay on top.
    const beforeId = map.getLayer('cs-dispensary-incoming-ring') ? 'cs-dispensary-incoming-ring' : undefined
    for (const spec of specs) {
      const src = map.getSource(spec.id) as GeoJSONSource | undefined
      const prev = applied.get(spec.id)
      if (!src) {
        map.addSource(spec.id, { type: 'geojson', data: spec.data as unknown as GeoJSON.FeatureCollection })
      } else if (prev?.data !== spec.data) {
        src.setData(spec.data as unknown as GeoJSON.FeatureCollection)
      }
      const layerIds: string[] = []
      for (const layer of spec.layers) {
        if (!map.getLayer(layer.id)) {
          map.addLayer({ ...layer, source: layer.source ?? spec.id } as Parameters<MapboxMap['addLayer']>[0], beforeId)
        }
        layerIds.push(layer.id)
      }
      applied.set(spec.id, { layerIds, data: spec.data })
    }
  }, [])

  const addSourcesAndLayers = useCallback((map: MapboxMap) => {
    if (!map.getStyle()) return
    if (!map.getSource(SOURCE.DISPENSARIES)) {
      map.addSource(SOURCE.DISPENSARIES, {
        type: 'geojson',
        data: latestDispensariesRef.current as unknown as GeoJSON.FeatureCollection,
        promoteId: 'id',
        cluster: true,
        clusterMaxZoom: 13,
        clusterRadius: 35,
      })
    }
    for (const spec of dispensaryLayerStack(propsRef.current.enrichedOnly, propsRef.current.pinMinZoom)) {
      if (!map.getLayer(spec.id)) {
        map.addLayer(spec as Parameters<MapboxMap['addLayer']>[0])
      }
    }
    // Page overlays were wiped by setStyle too — re-add them. Their applied
    // state is stale-but-harmless: getLayer/getSource guards re-create both.
    applyOverlays(map)
  }, [applyOverlays])

  const addSourcesAndLayersRef = useRef(addSourcesAndLayers)
  addSourcesAndLayersRef.current = addSourcesAndLayers

  // ── Map lifecycle — created ONCE, StrictMode-safe ──────────────────────────

  useEffect(() => {
    if (!isMapConfigured()) return
    if (mapObjRef.current) return
    const node = mapNodeRef.current
    if (!node) return

    mapboxgl.accessToken = MAPBOX_TOKEN
    const view = initialViewRef.current ?? CALIFORNIA_VIEWPORT
    const map = new mapboxgl.Map({
      container: node,
      style: initialStyleRef.current,
      center: [view.longitude, view.latitude],
      zoom: view.zoom,
    })
    mapObjRef.current = map
    appliedStyleRef.current = initialStyleRef.current

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')

    const updateBbox = () => {
      const b = map.getBounds()
      if (!b) return
      setBbox(`${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`)
    }

    const exposeForTests = () => {
      if (import.meta.env.MODE !== 'production') {
        ;(window as unknown as { __cs_map?: MapboxMap }).__cs_map = map
      }
    }
    exposeForTests() // available pre-load so tests can read the camera early

    // Force a post-layout repaint. Mapbox can mount at full canvas size with a
    // live GL context and 200-OK tiles yet never PAINT the first frame until a
    // resize fires — the GL context renders an empty frame before the container
    // has its final dimensions and nothing re-renders it (the blank-on-initial-
    // load bug). A double-rAF lands after the browser has laid out the container,
    // so resize() + triggerRepaint() paint the real frame WITHOUT any user
    // interaction. Runs on first style.load AND after every theme setStyle().
    const repaintAfterLayout = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const m = mapObjRef.current
          if (!m) return
          m.resize()
          m.triggerRepaint()
        })
      })
    }

    // "Alive" = the camera and getBounds are reliable. Announced once per
    // instance at the FIRST of styledata/idle/load: 'load' alone waits on
    // every initial tile, so a throttled Mapbox token could stall the
    // page-level fly-to (the CD slow-load centering gap) for 30s+.
    let announcedAlive = false
    const announceAlive = () => {
      if (announcedAlive) return
      announcedAlive = true
      propsRef.current.onMapLoad?.()
    }

    // Fires on initial style AND after every setStyle() — the single place
    // sources/layers/overlays are (re)added.
    map.on('style.load', () => {
      addSourcesAndLayersRef.current(map)
      exposeForTests()
      repaintAfterLayout()
    })
    map.on('styledata', announceAlive)
    map.once('idle', announceAlive)

    map.on('load', () => {
      map.resize()
      exposeForTests()
      setMapReady(true)
      updateBbox()
      propsRef.current.onMoveEnd?.({ zoom: map.getZoom() })
      announceAlive()
      // Belt-and-suspenders: 'load' can fire before the container settles its
      // final size; re-paint after layout so the first frame is never blank.
      repaintAfterLayout()
    })

    map.on('moveend', () => {
      updateBbox()
      propsRef.current.onMoveEnd?.({ zoom: map.getZoom() })
    })

    const interactiveLayersOn = () => {
      const ids = ['cs-dispensary-cluster', 'cs-dispensary-point', ...(propsRef.current.extraInteractiveLayers ?? [])]
      return ids.filter(id => map.getLayer(id))
    }

    map.on('click', (e) => {
      const layers = interactiveLayersOn()
      const features = layers.length ? map.queryRenderedFeatures(e.point, { layers }) : []
      const f = features[0]

      if (f?.layer?.id === 'cs-dispensary-cluster') {
        const clusterId = (f.properties as { cluster_id: number }).cluster_id
        const src = map.getSource(SOURCE.DISPENSARIES) as GeoJSONSource | undefined
        src?.getClusterExpansionZoom(clusterId, (err, z) => {
          if (err) return
          const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number]
          map.easeTo({ center: coords, zoom: z ?? 10, duration: 400 })
        })
        setDispPopup(null)
        return
      }

      if (f?.layer?.id === 'cs-dispensary-point') {
        const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number]
        const pinProps = f.properties as unknown as DispensaryFeatureProps
        setDispPopup({ lng: coords[0], lat: coords[1], props: pinProps })
        propsRef.current.onPinClick?.(pinProps, { lng: coords[0], lat: coords[1] })
        return
      }

      setDispPopup(null)
      propsRef.current.onMapClick?.(Object.assign(e, { features }))
    })

    map.on('mousemove', (e) => {
      if (!propsRef.current.onMapMouseMove) return
      const layers = interactiveLayersOn()
      const features = layers.length ? map.queryRenderedFeatures(e.point, { layers }) : []
      propsRef.current.onMapMouseMove(Object.assign(e, { features }))
    })

    map.on('mouseout', () => { propsRef.current.onMapMouseLeave?.() })

    // Hover feature-state for pins (drives the radius bump in the paint specs).
    let hoveredId: string | number | null = null
    const clearHover = () => {
      if (hoveredId !== null) {
        map.setFeatureState({ source: SOURCE.DISPENSARIES, id: hoveredId }, { hover: false })
        hoveredId = null
      }
    }
    map.on('mousemove', 'cs-dispensary-point', (e) => {
      const f = e.features?.[0]
      if (!f || f.id == null) return
      if (hoveredId !== null && hoveredId !== f.id) clearHover()
      hoveredId = f.id
      map.setFeatureState({ source: SOURCE.DISPENSARIES, id: f.id }, { hover: true })
      map.getCanvas().style.cursor = 'pointer'
    })
    map.on('mouseleave', 'cs-dispensary-point', () => {
      clearHover()
      map.getCanvas().style.cursor = propsRef.current.cursor ?? ''
    })
    map.on('mousemove', 'cs-dispensary-cluster', () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', 'cs-dispensary-cluster', () => { map.getCanvas().style.cursor = propsRef.current.cursor ?? '' })
    // Feature state dies with the old style on setStyle() — drop the stale
    // hover id so the next mousemove can't write hover state to the wrong pin.
    map.on('style.load', () => { hoveredId = null })

    return () => {
      // Full teardown — disposes the GL context and every source/layer at
      // once. No per-source removeSource → no updateTerrain crash path.
      // StrictMode: pass 1 creates map A, this destroys A; pass 2 creates B.
      map.remove()
      mapObjRef.current = null
      overlayStateRef.current.clear()
      // Marker/popup objects died with the map — drop them so the next map
      // (StrictMode pass 2) re-creates rather than reusing dead instances.
      markerObjsRef.current.clear()
      setMarkerEls({})
      popupObjRef.current = null
      setPopupContainer(null)
      hoverPopupObjRef.current = null
      setHoverContainer(null)
      setMapReady(false)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Theme / basemap switch — imperative, no remount ───────────────────────

  useEffect(() => {
    const map = mapObjRef.current
    // Gated on mapReady like every sibling effect: a setStyle issued before
    // the first 'load' can drop the just-added source (#3). A theme change
    // that lands early re-applies when mapReady flips true (dep below).
    if (!map || !mapReady) return
    const next = MAP_STYLES[mapStyleId][appTheme]
    if (appliedStyleRef.current === next) return
    appliedStyleRef.current = next
    map.setStyle(next) // style.load re-adds sources/layers/overlays
  }, [mapStyleId, appTheme, mapReady])

  // enrichedOnly toggles at runtime (heat-map filter) — the two point-layer
  // variants differ only in circle-opacity, so patch the paint in place.
  useEffect(() => {
    const map = mapObjRef.current
    if (!map || !mapReady || !map.getLayer('cs-dispensary-point')) return
    const variant = enrichedOnly ? dispensaryEnrichedOnlyPointLayer : dispensaryPointLayer
    const opacity = (variant.paint as Record<string, unknown>)['circle-opacity']
    map.setPaintProperty('cs-dispensary-point', 'circle-opacity', opacity as number)
  }, [enrichedOnly, mapReady])

  // Page-driven cursor (e.g. heat-map pointer over market bubbles).
  useEffect(() => {
    const map = mapObjRef.current
    if (!map || !mapReady) return
    map.getCanvas().style.cursor = cursor ?? ''
  }, [cursor, mapReady])

  // Keep canvas sized to the container.
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const ro = new ResizeObserver(() => { mapObjRef.current?.resize() })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Overlays — reconcile on prop change (style.load path is handled above).
  useEffect(() => {
    const map = mapObjRef.current
    if (!map || !mapReady || !map.getStyle()) return
    if (!map.isStyleLoaded()) return // setStyle in flight — style.load will apply
    applyOverlays(map)
  }, [overlays, mapReady, applyOverlays])

  // ── Markers — page DOM pins hosted in mapboxgl.Marker elements ───────────

  // Own-location markers are rendered HERE (once) for EVERY owned location, so
  // all three map pages inherit "every owned location pulses" without each
  // forking its own marker construction (MULTI_LOCATION_BILLING_SPEC §3 Phase
  // 2). They flow through the same mapboxgl.Marker + portal path as any
  // page-supplied marker. The currently-selected store (actionLocationId, set
  // by CompetitorDiscovery) gets a subtle emphasis; all locations still pulse.
  const ownLocationMarkerSpecs = useMemo<MapMarkerSpec[]>(
    () => locations
      .filter(l => l.lat != null && l.lng != null)
      .map(l => ({
        id: `own-loc:${l.id}`,
        longitude: Number(l.lng),
        latitude: Number(l.lat),
        element: <OwnLocationMarker title={l.name} emphasized={actionLocationId === l.id} />,
      })),
    [locations, actionLocationId],
  )
  // Internal own-location markers first, then any page-supplied markers.
  const allMarkers = useMemo<MapMarkerSpec[]>(
    () => [...ownLocationMarkerSpecs, ...(markers ?? [])],
    [ownLocationMarkerSpecs, markers],
  )

  const markerObjsRef = useRef<Map<string, mapboxgl.Marker>>(new Map())
  const [markerEls, setMarkerEls] = useState<Record<string, HTMLElement>>({})

  useEffect(() => {
    const map = mapObjRef.current
    if (!map || !mapReady) return
    const current = markerObjsRef.current
    const specs = allMarkers
    const wanted = new Set(specs.map(m => m.id))

    const removedIds: string[] = []
    const addedEls: Record<string, HTMLElement> = {}

    for (const [id, mk] of [...current]) {
      if (!wanted.has(id)) {
        mk.remove()
        current.delete(id)
        removedIds.push(id)
      }
    }
    for (const spec of specs) {
      let mk = current.get(spec.id)
      if (!mk) {
        const el = document.createElement('div')
        mk = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([spec.longitude, spec.latitude])
          .addTo(map)
        current.set(spec.id, mk)
        addedEls[spec.id] = el
      } else {
        mk.setLngLat([spec.longitude, spec.latitude])
      }
      const el = mk.getElement()
      el.onclick = spec.onClick ? () => spec.onClick?.() : null
    }
    if (removedIds.length || Object.keys(addedEls).length) {
      setMarkerEls(prev => {
        const next = { ...prev, ...addedEls }
        for (const id of removedIds) delete next[id]
        return next
      })
    }
  }, [allMarkers, mapReady])

  // ── Pin popup — one mapboxgl.Popup hosting React content via portal ──────

  const popupObjRef = useRef<mapboxgl.Popup | null>(null)
  const [popupContainer, setPopupContainer] = useState<HTMLDivElement | null>(null)
  // The "apply to other locations" matrix is collapsed by default so the popup
  // opens calm and short; it expands on demand. Reset on every new pin.
  const [applyOpen, setApplyOpen] = useState(false)
  useEffect(() => { setApplyOpen(false) }, [dispPopup])

  useEffect(() => {
    const map = mapObjRef.current
    if (!map || !mapReady) return
    if (!dispPopup) {
      popupObjRef.current?.remove()
      popupObjRef.current = null
      setPopupContainer(null)
      return
    }
    let popup = popupObjRef.current
    if (!popup) {
      const container = document.createElement('div')
      popup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        anchor: 'bottom',
        offset: [0, -10],
      }).setDOMContent(container)
      popup.on('close', () => setDispPopup(null))
      popupObjRef.current = popup
      setPopupContainer(container)
    }
    popup.setLngLat([dispPopup.lng, dispPopup.lat])
    if (!popup.isOpen()) popup.addTo(map)

    // ── Header-clearance pan ────────────────────────────────────────────────
    // The page header is an absolutely-positioned overlay floating over the top
    // of the map canvas. The popup is bottom-anchored (it grows UPWARD from the
    // pin), so a pin near the top edge would render its name/badges behind the
    // header. If — and only if — the clicked pin sits in that danger band, gently
    // drop it into a safe zone so the above-pin popup clears the header. The
    // popup is anchored to lngLat and rides along the pan; `moveend` does NOT
    // clear `dispPopup`, and the small downward pan keeps the pin inside the
    // viewport (so the appliedFc-leaves-view effect can't close it either) —
    // the popup stays open throughout.
    const SAFE_TOP = 72          // header-overlay zone over the canvas top (~64 + margin)
    const ANCHOR_GAP = 10        // matches the popup's bottom offset
    const POPUP_H_FALLBACK = 200 // estimate used only before content is laid out
    // Double rAF: wait for React to portal the popup content + lay it out so
    // offsetHeight is real before we decide whether (and how far) to pan.
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const m = mapObjRef.current
        const p = popupObjRef.current
        if (!m || !p || !p.isOpen()) return
        const el = p.getElement()
        const popupH = el && el.offsetHeight > 40 ? el.offsetHeight : POPUP_H_FALLBACK
        const pin = m.project([dispPopup.lng, dispPopup.lat])
        const popupTop = pin.y - ANCHOR_GAP - popupH
        if (popupTop >= SAFE_TOP) return  // already clears the header — no jitter
        const dy = SAFE_TOP - popupTop     // drop the pin down by this many px
        const c = m.getContainer()
        const newCenter = m.unproject([c.clientWidth / 2, c.clientHeight / 2 - dy])
        m.easeTo({ center: newCenter, duration: 300 })
      })
    })
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2) }
  }, [dispPopup, mapReady])

  // ── Hover popup (markets bubbles on the heat map) ─────────────────────────

  const hoverPopupObjRef = useRef<mapboxgl.Popup | null>(null)
  const [hoverContainer, setHoverContainer] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    const map = mapObjRef.current
    if (!map || !mapReady) return
    if (!hoverPopup) {
      hoverPopupObjRef.current?.remove()
      return
    }
    let popup = hoverPopupObjRef.current
    if (!popup) {
      const container = document.createElement('div')
      popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, anchor: 'bottom', offset: [0, -14] })
        .setDOMContent(container)
      hoverPopupObjRef.current = popup
      setHoverContainer(container)
    }
    popup.setLngLat([hoverPopup.longitude, hoverPopup.latitude])
    if (!popup.isOpen()) popup.addTo(map)
    const el = popup.getElement()
    if (el) el.style.pointerEvents = 'none'
  }, [hoverPopup, mapReady])

  // ── Imperative handle ─────────────────────────────────────────────────────

  const handle = useMemo<DispensaryMapHandle>(() => ({
    getMap: () => mapObjRef.current,
    flyTo: (options) => { mapObjRef.current?.flyTo(options) },
    easeTo: (options) => { mapObjRef.current?.easeTo(options) },
    resize: () => { mapObjRef.current?.resize() },
    setOverlayData: (id, data) => {
      const src = mapObjRef.current?.getSource(id) as GeoJSONSource | undefined
      src?.setData(data as unknown as GeoJSON.FeatureCollection)
    },
    toggleTrackBlock: (props, action, locationId) =>
      toggleTrackBlockRef.current(props, action, locationId),
    requestToggle: (props, action, locationId) =>
      requestToggleRef.current(props, action, locationId),
    removeSlots: (targets) => removeSlotsRef.current(targets),
  }), [])

  useImperativeHandle(forwardedRef, () => handle, [handle])
  // Also honor the `mapRef` prop pages already use. Assigned during render so
  // it's available before the map's async 'load' event fires.
  if (externalMapRef) externalMapRef.current = handle

  // ── Internal popup data (skipped when the page lifts it — RC2) ───────────

  useEffect(() => {
    if (!shouldLoadPopupState || !authFetch || usingExternalLocations) return
    let cancelled = false
    authFetch(`${API}/api/v1/locations`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return
        const locs = d.locations || d.data?.locations || []
        setInternalLocations(locs)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [authFetch, shouldLoadPopupState, usingExternalLocations])

  const fetchMergedCompetitors = useCallback(async (): Promise<PopupCompetitor[]> => {
    if (!authFetch || !locations.length) return []
    // Single bulk fetch — one round-trip for all locations (was an N+1 fan-out
    // of GET /locations/:id/competitors, one per location). Returns a
    // { [locationId]: Competitor[] } map; tag each row with its location so
    // removals can target the location that actually holds the slot (DELETE is
    // per-location).
    const ids = locations.map(l => l.id).join(',')
    try {
      const d = await authFetch(`${API}/api/v1/competitors/by-locations?locationIds=${ids}`).then(r => r.json())
      const byLocation = (d.data ?? d) as Record<string, PopupCompetitor[]>
      const rows = Object.entries(byLocation).flatMap(([locId, list]) =>
        (list || []).map(r => ({ ...r, location_id: locId }))
      )
      return mergeCompetitorRows(rows)
    } catch {
      return []
    }
  }, [authFetch, locations])

  useEffect(() => {
    if (!shouldLoadPopupState || usingExternalCompetitors || !locations.length) return
    let cancelled = false
    fetchMergedCompetitors().then(merged => { if (!cancelled) setInternalCompetitors(merged) })
    return () => { cancelled = true }
  }, [shouldLoadPopupState, usingExternalCompetitors, locations, fetchMergedCompetitors])

  useEffect(() => {
    if (!trackLocId && locations.length > 0) setTrackLocId(locations[0].id)
  }, [locations, trackLocId])

  // Page-pinned action target (CD's selected location) overrides the default.
  // An open popup was opened in the OLD location's context — close it so an
  // action can't silently retarget the new location.
  useEffect(() => {
    if (actionLocationId) setTrackLocId(actionLocationId)
    setDispPopup(null)
  }, [actionLocationId])

  // Close the popup when its feature leaves the applied FC (operator filter
  // change, bbox pan, delisting). A popup anchored to a pin that no longer
  // exists would otherwise show click-time state no other surface displays.
  useEffect(() => {
    if (!dispPopup) return
    if (!appliedFc.features.some(f => f.properties?.id === dispPopup.props.id)) {
      setDispPopup(null)
    }
  }, [dispPopup, appliedFc])

  // Surface the merged list to pages (single fetch path — RC2).
  useEffect(() => { onCompetitorsChange?.(competitors) }, [competitors]) // eslint-disable-line react-hooks/exhaustive-deps

  const refreshCompetitors = useCallback(async () => {
    if (externalRefreshCompetitors) {
      await externalRefreshCompetitors()
      return
    }
    const merged = await fetchMergedCompetitors()
    setInternalCompetitors(merged)
  }, [externalRefreshCompetitors, fetchMergedCompetitors])

  // ── Popup actions ─────────────────────────────────────────────────────────

  const showToast = useCallback((msg: string, color: string) => {
    setToast({ msg, color })
    setTimeout(() => setToast(null), 2800)
  }, [])

  // Optimistic pin recolor — OPERATOR-scoped: both track AND block patch every
  // pin of the clicked feature's OPERATOR (storefront + delivery of the same
  // store) and ONLY that store. A block is store-level, not a statewide brand
  // block (rescope from 2B). Each member keeps its own other-flag (a tracked
  // store that's now blocked becomes 'both'). Pushed through setData +
  // appliedFc; server truth follows via the internalRefresh refetch. Popup
  // badge/buttons re-derive from appliedFc — no separate sync needed.
  const patchGroupTrackState = useCallback((
    props: DispensaryFeatureProps,
    action: 'track' | 'block',
    removing: boolean,
  ) => {
    const fc = latestDispensariesRef.current
    if (!fc?.features?.length) return
    const isMember = (p?: DispensaryFeatureProps) => {
      if (!p) return false
      if (p.id === props.id) return true
      return Boolean(props.operator_id) && p.operator_id === props.operator_id
    }
    const patched = {
      ...fc,
      features: fc.features.map(f => {
        if (!isMember(f.properties)) return f
        const flags = trackStateToFlags(f.properties?.track_state)
        const next: TrackBlockFlags = action === 'track'
          ? { isTracked: !removing, isBlocked: flags.isBlocked }
          : { isTracked: flags.isTracked, isBlocked: !removing }
        return { ...f, properties: { ...f.properties, track_state: flagsToTrackState(next) } }
      }),
    }
    latestDispensariesRef.current = patched
    setAppliedFc(patched)
    const src = mapObjRef.current?.getSource(SOURCE.DISPENSARIES) as GeoJSONSource | undefined
    src?.setData(patched as unknown as GeoJSON.FeatureCollection)
  }, [])

  // Current flags for a pin, read from the applied FC — the same object the
  // pin layers paint from.
  const flagsOfFeature = useCallback((dispensaryId: string): TrackBlockFlags => {
    const f = latestDispensariesRef.current.features.find(f => f.properties?.id === dispensaryId)
    return trackStateToFlags(f?.properties?.track_state)
  }, [])

  // Competitor ids created from a pin this session — bridges the gap until
  // the next map refetch returns server-matched competitor_ids.
  const createdCompetitorIdsRef = useRef<Map<string, string>>(new Map())
  const competitorsListRef = useRef(competitors)
  competitorsListRef.current = competitors
  const locationsRef = useRef(locations)
  locationsRef.current = locations
  // Features with a toggle in flight — a second click (double-click, or a
  // sidebar click racing the disabled-state re-render) must not double-fire.
  const pendingTogglesRef = useRef<Set<string>>(new Set())

  const toggleTrackBlock = useCallback(async (
    props: DispensaryFeatureProps,
    action: 'track' | 'block',
    locationId?: string,
  ): Promise<boolean> => {
    if (!isTrackingAction(action) || !authFetch) return false
    const locId = locationId || trackLocId
    if (!locId) return false
    if (pendingTogglesRef.current.has(props.id)) return false
    pendingTogglesRef.current.add(props.id)
    setSavingTrack(true)
    try {
      // Resolve the competitor rows the SERVER matched to this pin's
      // operator — the client does no name/license matching of its own.
      // For unblock, also include the competitors actually HOLDING the
      // brand's block slots (2B: the block may live on a brand sibling —
      // e.g. unblocking a chain from a different store's pin).
      const ids = new Set(parseCompetitorIds(props.competitor_ids))
      if (action === 'block') {
        for (const id of parseCompetitorIds(props.block_competitor_ids)) ids.add(id)
      }
      const created = createdCompetitorIdsRef.current.get(props.id)
      if (created) ids.add(created)
      const entries = competitorsListRef.current.filter(c => ids.has(c.competitor_id))

      const cur = flagsOfFeature(props.id)
      const removing = action === 'track' ? cur.isTracked : cur.isBlocked

      if (removing) {
        // Remove the slot(s) AT THE SELECTED LOCATION only — state is now
        // per-store (§3 Phase 3). For a block this drops THIS store's
        // coverage; blocking.service releases the org-wide block_list (and
        // fires the CRM reactivation alert) only when it was the last block
        // slot anywhere in the org.
        const slotRows = entries.flatMap(e =>
          (e.slots ?? (e.slot_type ? [{ location_id: e.location_id, slot_type: e.slot_type }] : []))
            .filter(s => s.slot_type === action && (s.location_id ?? locId) === locId)
            .map(s => ({ competitor_id: e.competitor_id, location_id: locId }))
        )
        // Slots index not caught up (slot added moments ago) — fall back to the
        // selected location × known competitor ids. Misses 404 (benign); only
        // zero matches is a failure.
        const targets = slotRows.length
          ? slotRows
          : [...ids].map(competitor_id => ({ competitor_id, location_id: locId }))
        if (!targets.length) throw new Error('No competitor to remove')
        let okCount = 0
        for (const t of targets) {
          // slot_type is required for disambiguation: a (location, rival)
          // pair can hold a track row AND a block row (2A) — only the
          // action's own slot type may be removed.
          const res = await authFetch(
            `${API}/api/v1/locations/${t.location_id}/competitors/${t.competitor_id}?slot_type=${action}`,
            { method: 'DELETE' }
          )
          if (res.ok) okCount++
        }
        if (!okCount) throw new Error('Failed')
        showToast(action === 'block' ? `Unblocked ${props.name}` : `Untracked ${props.name}`, '#1d9e75')
      } else {
        // Any server-matched id works — `entries` only lists rivals with
        // ACTIVE slots, so an untracked rival resolves via the feature's
        // competitor_ids (no needless re-create round-trip).
        let competitorId = entries[0]?.competitor_id ?? [...ids][0]
        if (!competitorId) {
          const compRes = await authFetch(`${API}/api/v1/competitors`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: props.name,
              address: props.city,
              dcc_license: props.dcc_license ?? undefined,
              business_type: props.business_type ?? 'storefront',
              // Stable dispensary-id key — dcc_license is NULL for statewide
              // rows, and name-only matching mis-resolves same-name chains.
              google_place_id: props.dcc_license ?? `csd-${props.id}`,
            }),
          })
          const compData = await compRes.json()
          competitorId = compData.data?.id || compData.id
          if (competitorId) createdCompetitorIdsRef.current.set(props.id, competitorId)
        }
        if (!competitorId) throw new Error('Could not resolve competitor ID')
        // One slot at the selected location (§3 Phase 3 — per-store). For a
        // block, the server adds this store's coverage and reuses the single
        // org-level block_list row, so extra-store coverage never double-charges.
        const slotRes = await authFetch(`${API}/api/v1/locations/${locId}/competitors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ competitor_id: competitorId, slot_type: action }),
        })
        if (!slotRes.ok) throw new Error('Failed to add slot')
        showToast(action === 'block' ? `Blocked ${props.name}` : `Tracking ${props.name}`, '#1d9e75')
      }

      // Optimistic next state — track and block are INDEPENDENT slots (2A):
      // each action toggles only its own flag, and the patch covers the
      // whole operator (track) or brand (block) group (2B). Server truth
      // reconciles via the refetch below.
      patchGroupTrackState(props, action, removing)
      setInternalRefresh(k => k + 1)

      await refreshCompetitors()
      onPopupAction?.()
      return true
    } catch {
      showToast('Action failed — try again', '#d4537e')
      return false
    } finally {
      pendingTogglesRef.current.delete(props.id)
      setSavingTrack(false)
    }
  }, [authFetch, trackLocId, flagsOfFeature, refreshCompetitors, showToast, onPopupAction, patchGroupTrackState])

  const toggleTrackBlockRef = useRef(toggleTrackBlock)
  toggleTrackBlockRef.current = toggleTrackBlock

  // Batch slot removal for the slot-manager modal (A4). Reuses the exact
  // DELETE endpoint + reconciliation toggleTrackBlock's removal branch runs —
  // not a new mutation path. The modal supplies precise (location, competitor,
  // slot type) targets, so no pin props / flag resolution is needed here.
  const removeSlots = useCallback(async (
    targets: { locationId: string; competitorId: string; slotType: 'track' | 'block' }[],
  ): Promise<boolean> => {
    targets = targets.filter(target => target.slotType === 'track')
    if (!authFetch || !targets.length) return false
    setSavingTrack(true)
    let okCount = 0
    try {
      for (const t of targets) {
        const res = await authFetch(
          `${API}/api/v1/locations/${t.locationId}/competitors/${t.competitorId}?slot_type=${t.slotType}`,
          { method: 'DELETE' }
        )
        if (res.ok) okCount++
      }
      if (okCount) {
        // Same reconciliation as toggleTrackBlock: refetch the map GeoJSON
        // (server track_state recolors every affected pin) + canonical lists.
        setInternalRefresh(k => k + 1)
        await refreshCompetitors()
        onPopupAction?.()
        showToast(`Removed ${okCount} rival relationship${okCount === 1 ? '' : 's'}`, '#1d9e75')
      } else {
        showToast('Action failed — try again', '#d4537e')
      }
      return okCount > 0
    } catch {
      showToast('Action failed — try again', '#d4537e')
      return false
    } finally {
      setSavingTrack(false)
    }
  }, [authFetch, refreshCompetitors, onPopupAction, showToast])

  const removeSlotsRef = useRef(removeSlots)
  removeSlotsRef.current = removeSlots

  // ── Confirm-before-change ──────────────────────────────────────────────────
  // requestToggle opens the dialog (no slot change yet); the executor stored
  // on `confirm.run` performs the existing toggleTrackBlock path on confirm,
  // so the recolor + write happen AFTER confirmation.

  // The competitor ids an action touches: the pin's operator, plus (for a
  // block) the brand siblings actually holding block slots, plus any
  // competitor created from this pin this session. Same resolution
  // toggleTrackBlock uses, so the confirm preview matches what gets written.
  const resolveIds = useCallback((props: DispensaryFeatureProps, action: 'track' | 'block') => {
    const ids = new Set(parseCompetitorIds(props.competitor_ids))
    if (action === 'block') for (const id of parseCompetitorIds(props.block_competitor_ids)) ids.add(id)
    const created = createdCompetitorIdsRef.current.get(props.id)
    if (created) ids.add(created)
    return ids
  }, [])

  // Same-menu detection (§3 Phase 4). Asks the server for NEARBY rival pins whose
  // latest menu is an EXACT fingerprint match for the acted pin — never grouped
  // by operator name, only by empirical menu identity. Cached per pin/competitor
  // for the session; failures degrade to "no siblings" so the popup never blocks.
  const siblingCacheRef = useRef<Map<string, SameMenuPin[]>>(new Map())
  const fetchMenuSiblings = useCallback(async (
    props: DispensaryFeatureProps,
    action: 'track' | 'block',
  ): Promise<SameMenuPin[]> => {
    if (!authFetch) return []
    const compId = [...resolveIds(props, action)][0]
    const cacheKey = compId || `disp:${props.id}`
    const cached = siblingCacheRef.current.get(cacheKey)
    if (cached) return cached
    try {
      const qs = compId ? `competitor_id=${compId}` : `dispensary_id=${props.id}`
      const res = await authFetch(`${API}/api/v1/map/menu-siblings?${qs}`)
      const d = await res.json().catch(() => null)
      const siblings = (d?.data?.siblings ?? []) as SameMenuPin[]
      siblingCacheRef.current.set(cacheKey, siblings)
      return siblings
    } catch {
      return []
    }
  }, [authFetch, resolveIds])

  // Active per-(location, competitor) relationship facts for the resolved ids.
  const slotFacts = useCallback((ids: Set<string>) =>
    competitorsListRef.current
      .filter(c => ids.has(c.competitor_id))
      .flatMap(c => (c.slots ?? []).map(s => ({ comp: c.competitor_id, loc: s.location_id, type: s.slot_type })))
      .filter((s): s is { comp: string; loc: string; type: string } =>
        Boolean(s.loc) && (s.type === 'track' || s.type === 'block')),
  [])

  const requestToggle = useCallback((
    props: DispensaryFeatureProps,
    action: 'track' | 'block',
    locationId?: string,
  ) => {
    if (!isTrackingAction(action)) return
    const locId = locationId || trackLocId

    const ids = resolveIds(props, action)
    const facts = slotFacts(ids)
    const trackFacts = facts.filter(f => f.type === 'track')
    const blockFacts = facts.filter(f => f.type === 'block')

    // `removing` at the SELECTED store reads the pin's (optimistic) flags so
    // rapid re-clicks stay correct; for an "apply to other locations" target it
    // reads that store's per-location slot facts (the only source of its state).
    const targetIsSelected = locId === (actionLocationId ?? trackLocId)
    const cur = flagsOfFeature(props.id)
    const trackedThere = targetIsSelected ? cur.isTracked : trackFacts.some(f => f.loc === locId)
    const blockedThere = targetIsSelected ? cur.isBlocked : blockFacts.some(f => f.loc === locId)
    const removing = action === 'track' ? trackedThere : blockedThere
    const confirmAction = action === 'track'
      ? (removing ? 'untrack' : 'track')
      : (removing ? 'unblock' : 'block')
    let isLastBlock: boolean | undefined
    if (removing && action === 'block') {
      const coveredElsewhere = blockFacts.some(f => f.loc !== locId)
      isLastBlock = !coveredElsewhere
    }

    const locationName = locationsRef.current.find(l => l.id === locId)?.name
    setConfirm({
      data: {
        action: confirmAction, rivalName: props.name, locationName,
        isLastBlock,
      },
      run: () => toggleTrackBlockRef.current(props, action, locId).then(() => {}),
    })

    // Same-menu opt-in (§3 Phase 4) — ADD actions only. Fetched async so the
    // dialog appears instantly; the opt-in row pops in if the acted pin has
    // fingerprint-identical neighbors. Patches only the still-open dialog for
    // this rival (a later popup replaces `confirm` wholesale).
    if (!removing) {
      void fetchMenuSiblings(props, action).then(siblings => {
        if (!siblings.length) return
        setConfirm(prev =>
          prev && prev.data.rivalName === props.name && !prev.data.sameMenuPins
            ? { ...prev, data: { ...prev.data, sameMenuPins: siblings } }
            : prev
        )
      })
    }
  }, [trackLocId, actionLocationId, flagsOfFeature, resolveIds, slotFacts, fetchMenuSiblings])

  const requestToggleRef = useRef(requestToggle)
  requestToggleRef.current = requestToggle

  const runConfirm = useCallback((treatAsOne: boolean) => {
    if (!confirm) return
    // Opting in changes no billing. It is an acknowledgment that identical
    // menu pins represent one rival, so we
    // confirm it back to the user after the write lands.
    const groupNote = treatAsOne ? (confirm.data.sameMenuPins?.length ?? 0) : 0
    confirm.run().finally(() => {
      setConfirm(null)
      if (groupNote > 0) {
        showToast(`Monitoring as 1 rival — ${groupNote} identical-menu ${groupNote === 1 ? 'pin' : 'pins'} noted`, '#1d9e75')
      }
    })
  }, [confirm, showToast])

  // ── Derived ───────────────────────────────────────────────────────────────

  // Live popup props — the open popup's feature as it currently exists in
  // the applied FC (optimistic patches included). Falls back to the
  // click-time snapshot if the feature left the fetched bbox.
  const livePopupProps = useMemo<DispensaryFeatureProps | null>(() => {
    if (!dispPopup) return null
    const f = appliedFc.features.find(f => f.properties?.id === dispPopup.props.id)
    return (f?.properties as DispensaryFeatureProps | undefined) ?? dispPopup.props
  }, [dispPopup, appliedFc])

  // Badge AND buttons derive from these same flags — one source, no forks.
  const popupFlags = trackStateToFlags(livePopupProps?.track_state)

  // Per-store relationship for the open rival across ALL the org's locations —
  // derived from the server-matched competitor_ids + the merged per-location
  // slot list (§3 Phase 3). Drives the "tracked at <store>" / "blocked
  // org-wide" hint and the "Apply to other locations" control. Does NOT
  // re-match rivals client-side — competitor_ids come from the server.
  const rivalLocationStates = useMemo(() => {
    if (!livePopupProps) return [] as { id: string; name: string; isTracked: boolean; isBlocked: boolean }[]
    const ids = new Set(parseCompetitorIds(livePopupProps.competitor_ids))
    for (const id of parseCompetitorIds(livePopupProps.block_competitor_ids)) ids.add(id)
    const slots = competitors.filter(c => ids.has(c.competitor_id)).flatMap(c => c.slots ?? [])
    const trackLocs = new Set(slots.filter(s => s.slot_type === 'track' && s.location_id).map(s => s.location_id))
    const blockLocs = new Set(slots.filter(s => s.slot_type === 'block' && s.location_id).map(s => s.location_id))
    return locations.map(l => ({
      id: l.id, name: l.name,
      isTracked: trackLocs.has(l.id),
      isBlocked: blockLocs.has(l.id),
    }))
  }, [livePopupProps, competitors, locations])

  // The store the popup is acting on (selected). Other locations get the
  // "apply here too" control; this one is driven by the primary buttons.
  const popupSelectedLocId = actionLocationId ?? trackLocId
  const otherLocationStates = useMemo(
    () => rivalLocationStates.filter(l => l.id !== popupSelectedLocId),
    [rivalLocationStates, popupSelectedLocId],
  )
  // "tracked at <store>" hint sources — OTHER stores already tracking, and
  // whether the rival is blocked org-wide (one suppression covers the org).
  const trackedElsewhere = useMemo(
    () => otherLocationStates.filter(l => l.isTracked).map(l => l.name),
    [otherLocationStates],
  )
  const blockedOrgWide = useMemo(
    () => rivalLocationStates.some(l => l.isBlocked),
    [rivalLocationStates],
  )

  // Is the open pin one of the org's OWN stores? The dispensary GeoJSON layer
  // includes the operator's own locations (same physical store the purple
  // OwnLocationMarker already represents), so suppress track/block on a hit —
  // the product must never offer to charge the operator to spy on, or block,
  // their own store.
  //
  // SOURCE OF TRUTH: the server-computed `is_own` flag (operator_id /
  // platform_slug chain), which also catches the store's DELIVERY listing —
  // geocoded to a different point with no address, so the coordinate check
  // misses it. The isOwnLocation() coordinate helper stays ONLY as a fallback
  // for features whose is_own is absent (older cached tiles, or a listing with
  // a null operator_id). Match rule + threshold live in ownLocationMatch.ts.
  const popupIsOwnLocation = useMemo(() => {
    return dispPopup
      ? isOwnedRivalCandidate(livePopupProps?.is_own, dispPopup.lng, dispPopup.lat, locations)
      : false
  }, [dispPopup, livePopupProps, locations])

  const closePopup = useCallback(() => setDispPopup(null), [])

  // ── No-token fallback ─────────────────────────────────────────────────────

  if (!isMapConfigured()) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)', gap: 12, ...style }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Map unavailable</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', textAlign: 'center', maxWidth: 320, lineHeight: 1.6 }}>
          Set <code style={{ fontFamily: 'var(--mono)', fontSize: 11, background: 'var(--surface-3)', padding: '1px 5px', borderRadius: 3 }}>VITE_MAPBOX_TOKEN</code> in your environment.
        </div>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const defaultPopupContent = dispPopup && livePopupProps && authFetch && !renderPopup ? (
    <div style={{ minWidth: 236, maxWidth: 264, fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--text-1)', background: 'var(--surface-solid)', padding: '15px 16px', borderRadius: 12 }}>
      {/* Header — name leads, city + tags below. Editorial, matches the feed
          card head rather than a dense utility header. */}
      <div style={{ fontWeight: 700, fontSize: 14, letterSpacing: '-0.01em', marginBottom: 3 }}>{livePopupProps.name}</div>
      <div style={{ color: 'var(--text-3)', fontSize: 11.5, marginBottom: 11 }}>
        {livePopupProps.city}{livePopupProps.county ? `, ${livePopupProps.county} Co.` : ''}
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const, marginBottom: 13 }}>
        {livePopupProps.business_type && (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 8.5, fontWeight: 700, letterSpacing: '0.06em', padding: '3px 7px', borderRadius: 5, background: 'var(--surface-3)', color: 'var(--text-3)', textTransform: 'uppercase' as const }}>{livePopupProps.business_type}</span>
        )}
        {livePopupProps.market_tier && (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 8.5, fontWeight: 700, letterSpacing: '0.06em', padding: '3px 7px', borderRadius: 5, background: 'var(--accent-soft)', color: 'var(--accent)', textTransform: 'uppercase' as const }}>{livePopupProps.market_tier}</span>
        )}
        {popupFlags.isTracked && <span style={{ color: TRACK_STATE_COLORS.tracking }}>Tracking</span>}
      </div>
      {/* Own store — neutral, non-actionable. No rival language, no price, no
          Track/Block: this is the operator's own location (matched by
          coordinate proximity), already shown as the purple own-location
          marker. Uses the own-location purple to tie the two together. */}
      {popupIsOwnLocation && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4, padding: '9px 11px', background: 'rgba(139,92,246,0.10)', border: '1px solid rgba(139,92,246,0.30)', borderRadius: 9 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#8b5cf6', flexShrink: 0 }} />
          <span style={{ fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 600, color: '#8b5cf6' }}>This is your location</span>
        </div>
      )}
      {/* Everything below is competitor track/block — hidden on an
          own store so it can never be tracked, blocked, or priced. */}
      {!popupIsOwnLocation && (<>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 13 }}>
        <div style={{ fontFamily: 'var(--sans)', fontSize: 11, color: 'var(--text-2)' }}>Tracking is included in your location plan.</div>
        {livePopupProps.dcc_license && (
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', letterSpacing: '0.04em', marginTop: 3 }}>DCC {livePopupProps.dcc_license}</div>
        )}
      </div>
      {locations.length > 1 && !actionLocationId && (
        <select
          value={trackLocId}
          onChange={e => setTrackLocId(e.target.value)}
          style={{ width: '100%', marginBottom: 9, fontSize: 11.5, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-1)', padding: '6px 9px', fontFamily: 'var(--sans)' }}
        >
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      )}
      {/* Per-store hint (§3 Phase 3): the primary buttons act on the SELECTED
          store. If the rival is set up at another store but is a prospect here,
          say so — fixes the "looks already tracked" confusion on a new store. */}
      {!popupFlags.isTracked && trackedElsewhere.length > 0 && (
        <div style={{ fontSize: 10.5, color: 'var(--text-3)', marginBottom: 9, lineHeight: 1.5 }}>
          Tracked at {trackedElsewhere.join(', ')} — not {locations.find(l => l.id === popupSelectedLocId)?.name ?? 'this store'} yet.
        </div>
      )}
      {/* Primary actions — Track reads as the calm primary (accent-tinted, the
          feed's teal), Block as the ghost secondary. No hard divider above:
          spacing carries the separation so the area stops feeling like a
          control bar. */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          disabled={savingTrack}
          onClick={() => requestToggle(livePopupProps, 'track')}
          style={{
            flex: 1, padding: '8px 0', borderRadius: 9, fontSize: 11.5, fontWeight: 700,
            fontFamily: 'var(--sans)', cursor: savingTrack ? 'not-allowed' : 'pointer',
            border: `1px solid ${popupFlags.isTracked ? TRACK_STATE_COLORS.tracking : 'transparent'}`,
            background: popupFlags.isTracked ? TRACK_STATE_COLORS.tracking : 'var(--accent-soft)',
            color: popupFlags.isTracked ? '#fff' : 'var(--accent)',
            opacity: savingTrack ? 0.6 : 1,
          }}
        >{popupFlags.isTracked ? 'Untrack' : 'Track rival'}</button>

      </div>
      </>)}
      <button
        onClick={closePopup}
        style={{ marginTop: 11, width: '100%', fontSize: 11, padding: '6px 0', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--sans)', fontWeight: 500, border: 'none', background: 'transparent', color: 'var(--text-3)' }}
      >Done</button>
    </div>
  ) : null

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', ...style }}>
      <div ref={mapNodeRef} style={{ position: 'absolute', inset: 0 }} />

      {/* Markers — own-location pins (rendered here for every owned location)
          plus any page-supplied markers — portaled into mapboxgl.Marker elements */}
      {allMarkers.map(m =>
        markerEls[m.id] ? createPortal(m.element, markerEls[m.id], m.id) : null
      )}

      {/* Pin popup content — portaled into the mapboxgl.Popup container.
          renderPopup receives the LIVE props (applied FC, optimistic patches
          included), so page popups read the same truth as the pins. */}
      {dispPopup && popupContainer && createPortal(
        renderPopup ? renderPopup(livePopupProps ?? dispPopup.props, closePopup) : defaultPopupContent,
        popupContainer,
      )}

      {/* Hover popup content */}
      {hoverPopup && hoverContainer && createPortal(hoverPopup.content, hoverContainer)}

      {children}

      {showStyleToggle && (
        <div style={{ position: 'absolute', bottom: 16, right: 16, zIndex: 10, display: 'flex', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
          {(['streets', 'satellite'] as MapStyleId[]).map(id => (
            <button
              key={id}
              onClick={() => setMapStyleId(id)}
              style={{
                padding: '5px 11px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)',
                border: 'none', borderRight: id === 'streets' ? '1px solid var(--border)' : 'none',
                background: mapStyleId === id ? 'var(--accent)' : 'transparent',
                color: mapStyleId === id ? '#fff' : 'var(--text-2)',
                transition: 'background 0.12s, color 0.12s',
              }}
            >
              {id === 'streets' ? '🗺 Streets' : '🛰 Satellite'}
            </button>
          ))}
        </div>
      )}

      {toast && (
        <div
          onClick={() => setToast(null)}
          style={{ position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 40, background: toast.color, color: '#fff', fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, padding: '7px 16px', borderRadius: 20, whiteSpace: 'nowrap', boxShadow: '0 2px 12px rgba(0,0,0,0.3)', cursor: 'pointer' }}
        >{toast.msg}</div>
      )}

      {/* Confirm-before-change modal — covers the map panel. */}
      {confirm && (
        <SlotConfirmDialog
          data={confirm.data}
          busy={savingTrack}
          onConfirm={runConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}

// ── Public component ──────────────────────────────────────────────────────────

export const DispensaryMap = forwardRef<DispensaryMapHandle, DispensaryMapProps>(
  function DispensaryMap(props, ref) {
    return (
      <MapErrorBoundary>
        <DispensaryMapCore {...props} forwardedRef={ref} />
      </MapErrorBoundary>
    )
  }
)
